import type { Prayer } from '@/types/prayer';
import { containsRecoveredVoiceText } from '@/lib/audio-recovery';
import { useUnconfirmedSave } from '@/hooks/useUnconfirmedSave';
import UnconfirmedSaveWords from '@/components/UnconfirmedSaveWords';

/**
 * D4 (2026-10-18): read-only notice for a bound audio row of this prayer whose
 * save the server has not confirmed (a restored device finds the registry
 * record bound with `receipt_missing`). It shows the recovered words with Copy
 * and nothing else: no Retry, no Remove, no merge, no write. It renders in
 * read-only mode too. The "already appear in this prayer" line is a display
 * hint from text containment, never deletion authority. Presentation only: the
 * hook selects the row, the shared UnconfirmedSaveWords renders it.
 */
interface Props {
  prayer: Prayer;
}

export default function UnconfirmedSaveNotice({ prayer }: Props) {
  const candidate = useUnconfirmedSave({
    type: 'prayer',
    id: prayer.id,
    updatedAt: prayer.updatedAt,
  });
  if (!candidate) return null;
  const words = candidate.transcribedText?.trim() ?? '';
  const alreadyPresent =
    containsRecoveredVoiceText(prayer.rawText, words) ||
    (typeof prayer.testimony === 'string' &&
      containsRecoveredVoiceText(prayer.testimony, words)) ||
    prayer.progressHistory.some((entry) => containsRecoveredVoiceText(entry.note, words));

  return (
    <UnconfirmedSaveWords
      words={words}
      destination="prayer"
      alreadyPresent={alreadyPresent}
    />
  );
}
